
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { usePermissions } from '../../hooks/usePermissions';
import { IoArrowBack, IoAlertCircleOutline, IoChevronDown, IoSearch, IoAttachOutline, IoTrashOutline, IoCloudUploadOutline } from 'react-icons/io5';
import './CreateTaskPagestyle.css';
import { AccessDenied } from '../../shared-ui/States';

// --- Configuration from Environment Variables ---
const SWAGGER_BASE_URL = process.env.REACT_APP_SWAGGER_SERVICE_BASE_URL;
const NODE_BASE_URL = process.env.REACT_APP_NODE_SERVICE_BASE_URL;
const AUTH_TOKEN_KEY = 'zTaskAuthToken';

// --- API Helpers ---
const getApiHeaders = (isFormData = false) => {
    const headers = { 'Authorization': `Bearer ${localStorage.getItem(AUTH_TOKEN_KEY)}` };
    if (!isFormData) {
        headers['Content-Type'] = 'application/json';
        headers['Accept'] = 'application/json';
    }
    return headers;
};
const getUsers = async () => { const response = await fetch(`${SWAGGER_BASE_URL}/api/get-users`, { headers: getApiHeaders() }); if (!response.ok) throw new Error('Failed to fetch users'); const data = await response.json(); return data.userData || []; };
const createTask = async (payload) => { const response = await fetch(`${SWAGGER_BASE_URL}/api/create-task`, { method: 'POST', headers: getApiHeaders(), body: JSON.stringify(payload) }); if (!response.ok) { const err = await response.json().catch(() => ({})); throw new Error(err.message || 'Task creation failed'); } return response.json(); };
const getProjects = async () => { const response = await fetch(`${SWAGGER_BASE_URL}/api/get-project`, { headers: getApiHeaders() }); if (!response.ok) throw new Error('Failed to fetch projects'); const data = await response.json(); return data.projects; };
const getProjectsByCustomerId = async (customerId) => { const response = await fetch(`${SWAGGER_BASE_URL}/api/get-projects-by-customer-id?customerId=${customerId}`, { headers: getApiHeaders() }); if (!response.ok) throw new Error('Failed to fetch projects for customer'); return response.json(); }
const getCustomers = async () => { const response = await fetch(`${NODE_BASE_URL}/customer/api/customers/`, { headers: getApiHeaders() }); if (!response.ok) throw new Error('Failed to fetch customers'); return response.json(); };
const getColumnData = async (table, column) => { const response = await fetch(`${NODE_BASE_URL}/api/get-column/${table}/${column}`, { headers: getApiHeaders() }); if (!response.ok) throw new Error(`Failed to fetch data for ${table}/${column}`); return response.json(); };
const uploadAttachment = async (taskId, moduleName, file) => { const formData = new FormData(); formData.append('files', file); const response = await fetch(`${NODE_BASE_URL}/attachment/api/attachments/${taskId}/${moduleName}`, { method: 'POST', headers: getApiHeaders(true), body: formData }); if (!response.ok) { const err = await response.json().catch(() => ({})); throw new Error(err.message || 'File upload failed'); } return response.json(); };
const getFullHierarchyTree = async () => { const response = await fetch(`${SWAGGER_BASE_URL}/api-get-full-hierarchy-tree`, { headers: getApiHeaders() }); if (!response.ok) throw new Error('Failed to fetch hierarchy tree'); return response.json(); };
const getStatuses = () => getColumnData('common_master', 'current_status');
const getPriorities = () => getColumnData('common_master', 'priority_in_daily_task');
const getCategories = () => getColumnData('common_master', 'category');

// --- Constants & Helper Functions ---
const PERMISSIONS = { CREATE_TASK: 'CREATE TASK', VIEW_USER: 'VIEW USER', VIEW_PROJECT: 'VIEW PROJECT', VIEW_CATEGORY: 'VIEW CATEGORY', VIEW_STATUS: 'VIEW STATUS', VIEW_PRIORITY: 'VIEW PRIORITY' };
const pad = (num) => num.toString().padStart(2, '0');
const currentYear = new Date().getFullYear();
const endYear = 2099;
const yearRange = endYear - currentYear + 1;
const years = Array.from({ length: yearRange > 0 ? yearRange : 1 }, (_, i) => currentYear + i);
const allMonths = [ { value: '1', name: 'Jan' }, { value: '2', name: 'Feb' }, { value: '3', name: 'Mar' }, { value: '4', name: 'Apr' }, { value: '5', name: 'May' }, { value: '6', name: 'Jun' }, { value: '7', name: 'Jul' }, { value: '8', name: 'Aug' }, { value: '9', name: 'Sep' }, { value: '10', name: 'Oct' }, { value: '11', name: 'Nov' }, { value: '12', name: 'Dec' } ];
const getDaysInMonth = (year, month) => new Date(year, month, 0).getDate();
const allHours = Array.from({ length: 24 }, (_, i) => pad(i));
const allMinutes = Array.from({ length: 60 }, (_, i) => pad(i));
const autoCorrectGrammar = (text) => { if (typeof text !== 'string' || !text.trim() && text.length > 0) return text; let correctedText = text.trimStart(); if (correctedText.length > 0) { correctedText = correctedText.charAt(0).toUpperCase() + correctedText.slice(1); } return correctedText.replace(/([.!?]\s+)([a-z])/g, (match, p, letter) => p + letter.toUpperCase()); };

// --- Hierarchy Helper Functions ---
const findUserInHierarchy = (tree, userName) => {
    for (const node of tree) {
        if (node.name === userName) return node;
        if (node.children && node.children.length > 0) {
            const foundUser = findUserInHierarchy(node.children, userName);
            if (foundUser) return foundUser;
        }
    }
    return null;
};
const getAllSubordinates = (userNode) => {
    let subordinates = [];
    if (userNode && userNode.children && userNode.children.length > 0) {
        for (const child of userNode.children) {
            subordinates.push(child);
            subordinates = subordinates.concat(getAllSubordinates(child));
        }
    }
    return subordinates;
};

// --- Reusable Sub-Components ---
const CreateTaskPageSkeleton = () => ( <div className="create-task-page"><div className="page-header"><div className="shimmer-line back-link-skeleton"></div><div className="shimmer-line title-skeleton"></div><div className="shimmer-line subtitle-skeleton"></div></div><div className="create-task-form"><div className="form-main-panel"><div className="shimmer-line section-header-skeleton"></div>{[...Array(6)].map((_, i) => (<div key={i} className="form-group-skeleton"><div className="shimmer-line label-skeleton"></div><div className="shimmer-block input-skeleton"></div></div>))}<div className="form-group-skeleton"><div className="shimmer-line label-skeleton"></div><div className="shimmer-block textarea-skeleton"></div></div></div><div className="form-side-panel"><div className="shimmer-line section-header-skeleton"></div>{[...Array(4)].map((_, i) => (<div key={i} className="form-group-skeleton"><div className="shimmer-line label-skeleton"></div><div className="shimmer-block input-skeleton"></div></div>))}</div></div></div> );
const SearchableDropdown = ({
    options = [],
    selectedValues,
    onChange,
    placeholder,
    isMultiSelect = false,
    labelField = 'name',
    valueField = 'id',
    disabled = false,
    error = false,
    dataLoadError = null,
    allowClear = false,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [menuStyle, setMenuStyle] = useState({});
    const dropdownRef = useRef(null);
    const menuRef = useRef(null);

    const safeOptions = Array.isArray(options) ? options : [];
    const filteredOptions = safeOptions.filter((option) =>
        option && typeof option[labelField] === 'string'
            ? option[labelField].toLowerCase().includes(searchTerm.toLowerCase())
            : false
    );

    const handleSelect = (optionValue) => {
        if (isMultiSelect) {
            const current = Array.isArray(selectedValues) ? selectedValues : [];
            const newValues = current.includes(optionValue)
                ? current.filter((v) => v !== optionValue)
                : [...current, optionValue];
            onChange(newValues);
        } else {
            onChange(optionValue);
            setIsOpen(false);
            setSearchTerm('');
        }
    };

    const getDisplayValue = () => {
        if (isMultiSelect) {
            const current = Array.isArray(selectedValues) ? selectedValues : [];
            if (current.length === 0) return placeholder;
            if (current.length === 1) {
                const selectedOption = safeOptions.find((opt) => String(opt[valueField]) === String(current[0]));
                return selectedOption ? selectedOption[labelField] : '1 selected';
            }
            return `${current.length} items selected`;
        }
        const selectedOption = safeOptions.find((opt) => String(opt[valueField]) === String(selectedValues));
        return selectedOption ? selectedOption[labelField] : placeholder;
    };

    const updateMenuPosition = useCallback(() => {
        if (!dropdownRef.current) return;
        const rect = dropdownRef.current.getBoundingClientRect();
        setMenuStyle({
            position: 'absolute',
            top: rect.bottom + window.scrollY + 4,
            left: rect.left + window.scrollX,
            width: Math.max(rect.width, 220),
            zIndex: 10050,
        });
    }, []);

    useEffect(() => {
        if (!isOpen) return undefined;
        updateMenuPosition();
        const onReposition = () => updateMenuPosition();
        window.addEventListener('scroll', onReposition, true);
        window.addEventListener('resize', onReposition);
        return () => {
            window.removeEventListener('scroll', onReposition, true);
            window.removeEventListener('resize', onReposition);
        };
    }, [isOpen, updateMenuPosition, filteredOptions.length]);

    useEffect(() => {
        if (!isOpen) return undefined;
        const handleClickOutside = (event) => {
            const inTrigger = dropdownRef.current && dropdownRef.current.contains(event.target);
            const inMenu = menuRef.current && menuRef.current.contains(event.target);
            if (!inTrigger && !inMenu) {
                setIsOpen(false);
                setSearchTerm('');
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    return (
        <div className={`searchable-dropdown-container${isOpen ? ' is-open' : ''}`} ref={dropdownRef}>
            <button
                type="button"
                className={`dropdown-trigger ${error ? 'invalid-field' : ''}`}
                onClick={() => !disabled && setIsOpen((open) => !open)}
                disabled={disabled}
            >
                <span>{getDisplayValue()}</span>
                <IoChevronDown className={`dropdown-chevron ${isOpen ? 'open' : ''}`} />
            </button>
            {isOpen &&
                createPortal(
                    <div
                        className="dropdown-panel ui-portal-dropdown-menu create-task-dropdown-menu"
                        style={menuStyle}
                        ref={menuRef}
                    >
                        <div className="dropdown-search-container ui-portal-dropdown-search">
                            <input
                                type="text"
                                className="dropdown-search-input"
                                placeholder="Search..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                autoFocus
                                onClick={(e) => e.stopPropagation()}
                            />
                            <IoSearch className="search-icon" aria-hidden="true" />
                        </div>
                        <ul className="dropdown-options-list">
                            {dataLoadError && (
                                <li className="dropdown-option-message">{dataLoadError}</li>
                            )}
                            {!dataLoadError && allowClear && !isMultiSelect && (
                                <li className="dropdown-option clear-option" onClick={() => handleSelect('')}>
                                    — Clear Selection —
                                </li>
                            )}
                            {!dataLoadError && filteredOptions.length > 0
                                ? filteredOptions.map((option) => {
                                      const isSelected = isMultiSelect
                                          ? (Array.isArray(selectedValues) ? selectedValues : []).includes(option[valueField])
                                          : String(selectedValues) === String(option[valueField]);
                                      return (
                                          <li
                                              key={String(option[valueField])}
                                              className={`dropdown-option ${isSelected ? 'selected' : ''}`}
                                              onClick={() => handleSelect(option[valueField])}
                                          >
                                              {isMultiSelect && (
                                                  <input
                                                      type="checkbox"
                                                      checked={isSelected}
                                                      readOnly
                                                      className="dropdown-checkbox"
                                                  />
                                              )}
                                              {option[labelField]}
                                          </li>
                                      );
                                  })
                                : !dataLoadError && (
                                      <li className="dropdown-option-message">No results found</li>
                                  )}
                        </ul>
                    </div>,
                    document.body
                )}
        </div>
    );
};

// --- Main Page Component ---
const CreateTaskPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, hasPermission, isLoading: isPermissionsLoading } = usePermissions();
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [selectedAssigneeId, setSelectedAssigneeId] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('');
    const [selectedPriority, setSelectedPriority] = useState('');
    const [dueDate, setDueDate] = useState(new Date());
    const [filteredMonths, setFilteredMonths] = useState(allMonths);
    const [filteredDays, setFilteredDays] = useState([]);
    const [filteredHours, setFilteredHours] = useState(allHours);
    const [filteredMinutes, setFilteredMinutes] = useState(allMinutes);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedAssetIds, setSelectedAssetIds] = useState([]);
    const [remindMeDays, setRemindMeDays] = useState(1);
    const [maxReminderDays, setMaxReminderDays] = useState(365);
    const [files, setFiles] = useState([]);
    const [selectedCustomerId, setSelectedCustomerId] = useState('');
    const [selectedProject, setSelectedProject] = useState('');
    const [assets, setAssets] = useState([]);
    const [assignees, setAssignees] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [priorities, setPriorities] = useState([]);
    const [categories, setCategories] = useState([]);
    const [allProjects, setAllProjects] = useState([]);
    const [filteredProjects, setFilteredProjects] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [selectedDepartment, setSelectedDepartment] = useState('');
    const [isFetchingInitialData, setIsFetchingInitialData] = useState(true);
    const [isFetchingProjects, setIsFetchingProjects] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [dataLoadErrors, setDataLoadErrors] = useState({});
    const [formValidationError, setFormValidationError] = useState(null);
    const [invalidFields, setInvalidFields] = useState([]);
    const [successMessage, setSuccessMessage] = useState('');
    const [initialDataLoadAttempted, setInitialDataLoadAttempted] = useState(false);
    const [hierarchy, setHierarchy] = useState(null);
    const [formattedAssignees, setFormattedAssignees] = useState([]);
    const [isDragging, setIsDragging] = useState(false);
    
    const taskToClone = location.state?.taskToClone;

    const fetchInitialData = useCallback(async (loggedInUser) => {
        setIsFetchingInitialData(true); setDataLoadErrors({}); setInitialDataLoadAttempted(true);
        const canViewUsers = hasPermission(PERMISSIONS.VIEW_USER);
        const selfUserObject = { id: String(loggedInUser.id), name: loggedInUser.name, email: loggedInUser.email, department: loggedInUser.department };
        const dataFetchers = { users: canViewUsers ? getUsers() : Promise.resolve([selfUserObject]), statuses: hasPermission(PERMISSIONS.VIEW_STATUS) ? getStatuses() : Promise.resolve(null), priorities: hasPermission(PERMISSIONS.VIEW_PRIORITY) ? getPriorities() : Promise.resolve(null), categories: hasPermission(PERMISSIONS.VIEW_CATEGORY) ? getCategories() : Promise.resolve(null), projects: hasPermission(PERMISSIONS.VIEW_PROJECT) ? getProjects() : Promise.resolve(null), customers: hasPermission(PERMISSIONS.VIEW_PROJECT) ? getCustomers() : Promise.resolve(null), assetNames: getColumnData('assets', 'name'), assetRegNumbers: getColumnData('assets', 'registration_number'), assetSerialNumbers: getColumnData('assets', 'serial_number'), assetCodes: getColumnData('assets', 'asset_code'), hierarchy: getFullHierarchyTree() };
        const results = await Promise.allSettled(Object.values(dataFetchers));
        const [ usersRes, statusesRes, prioritiesRes, categoriesRes, projectsRes, customersRes, assetNamesRes, assetRegNumbersRes, assetSerialNumbersRes, assetCodesRes, hierarchyRes ] = results;
        if (statusesRes.status === 'fulfilled' && statusesRes.value) { const formatted = (statusesRes.value || []).map(s => ({ ...s, displayValue: s.value.replace(/_/g, ' ') })); const open = formatted.find(s => s.value.toLowerCase() === 'open'); setSelectedStatus(open ? open.value : (formatted.length > 0 ? formatted[0].value : '')); setStatuses(formatted); } else { setDataLoadErrors(prev => ({ ...prev, statuses: 'No permission to view statuses.' })); }
        if (prioritiesRes.status === 'fulfilled' && prioritiesRes.value) { const medium = prioritiesRes.value.find(p => p.value.toUpperCase() === 'MEDIUM'); setSelectedPriority(medium ? medium.value : (prioritiesRes.value.length > 0 ? prioritiesRes.value[0].value : '')); setPriorities(prioritiesRes.value); } else { setDataLoadErrors(prev => ({ ...prev, priorities: 'No permission to view priorities.' })); }
        if (categoriesRes.status === 'fulfilled' && categoriesRes.value) { setCategories(categoriesRes.value); if (categoriesRes.value.length > 0) { setSelectedCategory(categoriesRes.value[0].value); } } else { setDataLoadErrors(prev => ({ ...prev, categories: 'No permission to view categories.' })); }
        if (usersRes.status === 'fulfilled') { setAssignees((usersRes.value || []).map(u => ({ ...u, id: String(u.id) }))); } else { setDataLoadErrors(prev => ({ ...prev, users: 'Could not load users.' })); }
        if (projectsRes.status === 'fulfilled' && projectsRes.value) { const allFetchedProjects = (projectsRes.value || []).map(p => ({ ...p, id: String(p.projectId), name: p.projectName })); setAllProjects(allFetchedProjects); setFilteredProjects(allFetchedProjects); } else { setDataLoadErrors(prev => ({ ...prev, projects: 'No permission to view projects.' })); }
        if (customersRes.status === 'fulfilled' && customersRes.value) { setCustomers((customersRes.value || []).filter(c => c.active).map(c => ({ id: String(c.id), name: c.name }))); } else { setDataLoadErrors(prev => ({ ...prev, customers: 'No permission to view customers.' })); }
        if (assetNamesRes.status === 'fulfilled' && assetRegNumbersRes.status === 'fulfilled' && assetSerialNumbersRes.status === 'fulfilled' && assetCodesRes.status === 'fulfilled') {
            const mergedAssets = (assetNamesRes.value || []).map(nameAsset => {
                const serialAsset = (assetSerialNumbersRes.value || []).find(s => s.id === nameAsset.id);
                const codeAsset = (assetCodesRes.value || []).find(c => c.id === nameAsset.id);
                return { 
                    id: nameAsset.id, 
                    name: nameAsset.value, 
                    value: `${nameAsset.value} | ${serialAsset?.value || 'N/A'} | ${codeAsset?.value || 'N/A'}` 
                };
            });
            setAssets(mergedAssets);
        } else {
            setDataLoadErrors(prev => ({ ...prev, assets: 'Could not load asset details.' }));
        }
        if (hierarchyRes.status === 'fulfilled' && hierarchyRes.value) { setHierarchy(hierarchyRes.value?.tree || []); } else { setDataLoadErrors(prev => ({...prev, hierarchy: 'Could not load hierarchy.'})); }
        if (loggedInUser) { setSelectedAssigneeId(String(loggedInUser.id)); if (usersRes.status === 'fulfilled' && usersRes.value) { const defaultUser = usersRes.value.find(u => String(u.id) === String(loggedInUser.id)); setSelectedDepartment(defaultUser?.department?.departmentName || 'N/A'); } else { setSelectedDepartment(loggedInUser.department?.departmentName || 'N/A'); } }
        setIsFetchingInitialData(false);
    }, [hasPermission]);

    useEffect(() => { if (!isPermissionsLoading && user && !initialDataLoadAttempted) { if (hasPermission(PERMISSIONS.CREATE_TASK)) { fetchInitialData(user); } else { setIsFetchingInitialData(false); } } }, [user, isPermissionsLoading, initialDataLoadAttempted, hasPermission, fetchInitialData]);

    useEffect(() => {
        if (!isFetchingInitialData && taskToClone) {
            setTitle(`Clone of: ${taskToClone.title}`);
            setDescription(taskToClone.description || '');
            setSelectedCategory(taskToClone.category || '');
            setSelectedPriority(taskToClone.priority || '');
            
            if (taskToClone.assigneeId) {
                setSelectedAssigneeId(String(taskToClone.assigneeId));
                const assignee = assignees.find(u => String(u.id) === String(taskToClone.assigneeId));
                if (assignee) {
                    setSelectedDepartment(assignee.department?.departmentName || 'N/A');
                }
            }

            if (taskToClone.dueDate) {
                const clonedDueDate = new Date(taskToClone.dueDate);
                if (clonedDueDate > new Date()) {
                    setDueDate(clonedDueDate);
                }
            }

            if (taskToClone.projectName && allProjects.length > 0) {
                const project = allProjects.find(p => p.name === taskToClone.projectName);
                if (project) {
                    setSelectedProject(project.id);
                    if (project.customerId) {
                        setSelectedCustomerId(String(project.customerId));
                    }
                }
            }
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [isFetchingInitialData, taskToClone, allProjects, assignees, navigate, location.pathname]);

    useEffect(() => {
        if (!user || !hierarchy || !assignees.length) {
            setFormattedAssignees([]);
            return;
        }

        const userRole = user?.role?.name.toUpperCase();
        if (userRole === 'SUPER_ADMIN' || userRole === 'ADMIN') {
            setFormattedAssignees(assignees.map(u => ({ id: String(u.id), name: u.name })));
            return;
        }

        const currentUserNode = findUserInHierarchy(hierarchy, user.name);
        if (currentUserNode) {
            const subordinateUsers = getAllSubordinates(currentUserNode);
            const subordinateIds = new Set(subordinateUsers.map(u => String(u.id)));
            const filteredList = assignees.filter(assignee =>
                String(assignee.id) === String(currentUserNode.id) || 
                subordinateIds.has(String(assignee.id))
            );
            setFormattedAssignees(filteredList.map(u => ({ id: String(u.id), name: u.name })));
        } else {
            const self = assignees.find(u => String(u.id) === String(user.id));
            setFormattedAssignees(self ? [{ id: String(self.id), name: self.name }] : []);
        }
    }, [user, hierarchy, assignees]);

    useEffect(() => {
        const now = new Date();
        if (dueDate < now) { setDueDate(now); return; }
        const year = dueDate.getFullYear(); const month = dueDate.getMonth() + 1; const day = dueDate.getDate(); const hour = dueDate.getHours();
        const isCurrentYear = year === now.getFullYear(); const isCurrentMonth = isCurrentYear && month === (now.getMonth() + 1); const isCurrentDay = isCurrentMonth && day === now.getDate(); const isCurrentHour = isCurrentDay && hour === now.getHours();
        setFilteredMonths(isCurrentYear ? allMonths.filter(m => parseInt(m.value) >= (now.getMonth() + 1)) : allMonths);
        const daysInMonth = getDaysInMonth(year, month); const allDaysInMonth = Array.from({ length: daysInMonth }, (_, i) => (i + 1).toString());
        setFilteredDays(isCurrentMonth ? allDaysInMonth.filter(d => parseInt(d) >= now.getDate()) : allDaysInMonth);
        setFilteredHours(isCurrentDay ? allHours.filter(h => parseInt(h) >= now.getHours()) : allHours);
        setFilteredMinutes(isCurrentHour ? allMinutes.filter(m => parseInt(m) >= now.getMinutes()) : allMinutes);
        const today = new Date(); today.setHours(0, 0, 0, 0); const selectedDateOnly = new Date(year, month - 1, day);
        if (selectedDateOnly < today) { setMaxReminderDays(0); if (remindMeDays !== 1) setRemindMeDays(1); }
        else { const diffTime = selectedDateOnly.getTime() - today.getTime(); const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)); setMaxReminderDays(diffDays); if (remindMeDays > diffDays) { setRemindMeDays(diffDays > 0 ? diffDays : 1); } }
    }, [dueDate, remindMeDays]);

    const handleDateTimeChange = (part, value) => {
        const newDate = new Date(dueDate);
        switch (part) {
            case 'year': newDate.setFullYear(parseInt(value, 10)); break;
            case 'month': newDate.setMonth(parseInt(value, 10) - 1); break;
            case 'day': newDate.setDate(parseInt(value, 10)); break;
            case 'hour': newDate.setHours(parseInt(value, 10)); break;
            case 'minute': newDate.setMinutes(parseInt(value, 10)); break;
            default: break;
        }
        setDueDate(newDate);
    };

    const handleFieldChange = (fieldName, value, setterFunction) => { setterFunction(value); if (invalidFields.includes(fieldName)) { const updatedInvalidFields = invalidFields.filter(f => f !== fieldName); setInvalidFields(updatedInvalidFields); if (updatedInvalidFields.length === 0 && formValidationError) { setFormValidationError(null); } } };
    const handleAssigneeChange = (userId) => { handleFieldChange('assignee', userId, setSelectedAssigneeId); const selectedUser = assignees.find(user => String(user.id) === String(userId)); setSelectedDepartment(selectedUser ? (selectedUser.department?.departmentName || 'N/A') : 'N/A'); };
    const handleCustomerChange = async (customerId) => { setSelectedCustomerId(customerId); setSelectedProject(''); setFilteredProjects(customerId ? [] : allProjects); setDataLoadErrors(prev => ({...prev, projects: null})); if (customerId) { setIsFetchingProjects(true); try { const data = await getProjectsByCustomerId(customerId); const customerProjects = (data.projects || []).map(p => ({ ...p, id: String(p.projectId), name: p.projectName })); setFilteredProjects(customerProjects); } catch (error) { console.error("Failed to fetch projects for customer", error); setDataLoadErrors(prev => ({ ...prev, projects: 'Could not load projects for this customer.' })); setFilteredProjects([]); } finally { setIsFetchingProjects(false); } } else { setFilteredProjects(allProjects); } };
    const handleProjectChange = (projectId) => { setSelectedProject(projectId); if (projectId) { const project = allProjects.find(p => String(p.id) === String(projectId)); if (project && project.customerId && String(project.customerId) !== selectedCustomerId) { setSelectedCustomerId(String(project.customerId)); } } };
    
    const addFilesToState = useCallback((newFiles) => {
        setFiles(prevFiles => {
            const existingFileNames = new Set(prevFiles.map(f => f.name));
            const uniqueNewFiles = newFiles.filter(nf => !existingFileNames.has(nf.name));
            return [...prevFiles, ...uniqueNewFiles];
        });
    }, []);

    const handleFileChange = (e) => {
        if (e.target.files) {
            addFilesToState(Array.from(e.target.files));
        }
        e.target.value = null; // Allow selecting the same file again
    };

    const handleRemoveFile = (fileNameToRemove) => {
        setFiles(prevFiles => prevFiles.filter(file => file.name !== fileNameToRemove));
    };

    // --- Drag and Drop Handlers ---
    const handleFileDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            addFilesToState(Array.from(e.dataTransfer.files));
            e.dataTransfer.clearData();
        }
    }, [addFilesToState]);

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const handleDragEnter = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
            setIsDragging(true);
        }
    }, []);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!hasPermission(PERMISSIONS.CREATE_TASK)) { setFormValidationError("You do not have permission to perform this action."); return; }
        setFormValidationError(null); setInvalidFields([]); setSuccessMessage('');
        const currentInvalidFields = [];
        if (!title.trim()) { currentInvalidFields.push('title'); }
        if (!selectedAssigneeId) { currentInvalidFields.push('assignee'); }
        if (!selectedStatus) { currentInvalidFields.push('status'); }
        if (!selectedPriority) { currentInvalidFields.push('priority'); }
        if (!selectedCategory) { currentInvalidFields.push('category'); }
        if (!dueDate) { currentInvalidFields.push('dueDate'); }
        if (currentInvalidFields.length > 0) { setFormValidationError('Please fill in all required fields (*).'); setInvalidFields(currentInvalidFields); return; }
        setIsSubmitting(true);
        const selectedUserObject = assignees.find(user => String(user.id) === String(selectedAssigneeId));
        if (!selectedUserObject) { setFormValidationError("Error: The selected assignee could not be found."); setInvalidFields(prev => [...prev, 'assignee']); setIsSubmitting(false); return; }

        const finalDueDate = new Date(dueDate);
        const now = new Date();
        if (finalDueDate < now) { finalDueDate.setMinutes(now.getMinutes()); }

        const year = finalDueDate.getFullYear();
        const month = pad(finalDueDate.getMonth() + 1);
        const day = pad(finalDueDate.getDate());
        const hour = pad(finalDueDate.getHours());
        const minute = pad(finalDueDate.getMinutes());

        const combinedDateTime = `${year}-${month}-${day}T${hour}:${minute}:00.000Z`;
        const selectedAssetNames = selectedAssetIds.map(id => assets.find(a => a.id === id)?.name).filter(Boolean);
        const taskPayload = { title: autoCorrectGrammar(title).trim(), description: autoCorrectGrammar(description).trim(), status: selectedStatus.toLowerCase(), priority: selectedPriority, assigneeName: selectedUserObject.name, dueDate: combinedDateTime, category: selectedCategory, projectName: allProjects.find(p => String(p.id) === String(selectedProject))?.name || "", assetName: selectedAssetNames, reminderDays: remindMeDays, customerId: selectedCustomerId ? parseInt(selectedCustomerId, 10) : 0, comments: "", remarks: "" };

        try {
            const createTaskResponse = await createTask(taskPayload);
            const newTaskId = createTaskResponse?.newTask?.taskId;
            if (!newTaskId) { throw new Error("Task created, but a task ID was not returned."); }
            if (files.length > 0) { 
                const uploadPromises = files.map(file => uploadAttachment(newTaskId, 'attachment_master', file)); 
                const uploadResults = await Promise.allSettled(uploadPromises); 
                const failedUploads = uploadResults.map((result, index) => ({ ...result, name: files[index].name })).filter(item => item.status === 'rejected'); 
                if (failedUploads.length > 0) { 
                    setFormValidationError(`Task created, but failed to upload: ${failedUploads.map(f => f.name).join(', ')}.`); 
                } 
            }
            setSuccessMessage('Task created successfully! Redirecting...');
            setTimeout(() => { navigate('/tasks'); }, 2000);
        } catch (err) { const errorMessage = err.response?.data?.message || err.message || 'An unknown API error occurred.'; setFormValidationError(errorMessage); setIsSubmitting(false); }
    };
    
    if (isPermissionsLoading || isFetchingInitialData) { return <CreateTaskPageSkeleton />; }
    if (!hasPermission(PERMISSIONS.CREATE_TASK)) {
        return (
            <div className="create-task-page">
                <div className="page-header">
                    <Link to="/tasks" className="back-link"><IoArrowBack /> Back to Tasks</Link>
                </div>
                <AccessDenied description="You do not have the required permission to create a new task. Please contact your administrator." />
            </div>
        );
    }

    const isSuperAdmin = ['SUPER_ADMIN', 'ADMIN'].includes(String(user?.role?.name || '').toUpperCase());
    const isAssigneeFieldDisabled = isSubmitting || !hasPermission(PERMISSIONS.VIEW_USER) || (!isSuperAdmin && formattedAssignees.length <= 1);

    return (
        <div className="create-task-page">
            <div className="page-header"><Link to="/tasks" className="back-link"><IoArrowBack /> Back to Tasks</Link><h1>Create New Task</h1><p>Fill details to create a new task.</p></div>
            {successMessage && <div className="success-message" role="alert">{successMessage}</div>}
            {formValidationError && <div className="error-message icon-flex" role="alert" style={{ marginBottom: '15px' }}><IoAlertCircleOutline size={20} />{formValidationError}</div>}
            <form onSubmit={handleSubmit} className="create-task-form" noValidate>
                <div className="form-main-panel">
                    <h2>Task Information</h2>
                    <div className="form-group"><label htmlFor="category">Category <span className="required-asterisk">*</span></label><SearchableDropdown options={categories} selectedValues={selectedCategory} onChange={(value) => handleFieldChange('category', value, setSelectedCategory)} placeholder="Select a Category" labelField="value" valueField="value" disabled={isSubmitting || !hasPermission(PERMISSIONS.VIEW_CATEGORY)} error={invalidFields.includes('category')} dataLoadError={dataLoadErrors.categories} />{invalidFields.includes('category') && <small className="field-error-message">Category is required.</small>}</div>
                    <div className="form-group"><label htmlFor="title">Title <span className="required-asterisk">*</span></label><input type="text" id="title" value={title} onChange={(e) => handleFieldChange('title', autoCorrectGrammar(e.target.value), setTitle)} placeholder="Enter task title" required disabled={isSubmitting} className={invalidFields.includes('title') ? 'invalid-field' : ''} />{invalidFields.includes('title') && <small className="field-error-message">Title is required.</small>}</div>
                    <div className="form-group"><label htmlFor="description">Description</label><textarea id="description" value={description} onChange={(e) => setDescription(autoCorrectGrammar(e.target.value))} placeholder="Provide a detailed description" rows="4" disabled={isSubmitting} /></div>
                    <div className="form-group">
                        <label>Customer (Optional)</label>
                        <SearchableDropdown options={customers} selectedValues={selectedCustomerId} onChange={handleCustomerChange} placeholder="Select a Customer" labelField="name" valueField="id" disabled={isSubmitting || !hasPermission(PERMISSIONS.VIEW_PROJECT)} dataLoadError={dataLoadErrors.customers} allowClear={true}/>
                        {dataLoadErrors.customers && <small className="field-error-message">{dataLoadErrors.customers}</small>}
                        {hasPermission(PERMISSIONS.VIEW_PROJECT) && (
                            <div className="field-action-container">
                                <Link to="/customers/new" className="field-action-button">+ Create New Customer</Link>
                            </div>
                        )}
                    </div>
                    <div className="form-group">
                        <label>Project (Optional)</label>
                        <SearchableDropdown options={filteredProjects} selectedValues={selectedProject} onChange={handleProjectChange} placeholder={isFetchingProjects ? "Loading projects..." : "Select a Project"} labelField="name" valueField="id" disabled={isSubmitting || isFetchingProjects || !hasPermission(PERMISSIONS.VIEW_PROJECT)} dataLoadError={dataLoadErrors.projects} allowClear={true}/>
                        {dataLoadErrors.projects && <small className="field-error-message">{dataLoadErrors.projects}</small>}
                        {hasPermission(PERMISSIONS.VIEW_PROJECT) && (
                            <div className="field-action-container">
                                <Link to="/project" className="field-action-button">+ Create New Project</Link>
                            </div>
                        )}
                    </div>
                    <div className="form-group"><label>Assets (Optional)</label><SearchableDropdown options={assets} selectedValues={selectedAssetIds} onChange={(values) => handleFieldChange('assets', values, setSelectedAssetIds)} placeholder="Select Assets" isMultiSelect={true} labelField="value" valueField="id" disabled={isSubmitting} dataLoadError={dataLoadErrors.assets}/>{dataLoadErrors.assets && <small className="field-error-message">{dataLoadErrors.assets}</small>}</div>
                    <div className="form-row"><div className="form-group"><label htmlFor="status">Status <span className="required-asterisk">*</span></label><SearchableDropdown options={statuses} selectedValues={selectedStatus} onChange={(value) => handleFieldChange('status', value, setSelectedStatus)} placeholder="Select a Status" labelField="displayValue" valueField="value" disabled={isSubmitting || !hasPermission(PERMISSIONS.VIEW_STATUS)} error={invalidFields.includes('status')} dataLoadError={dataLoadErrors.statuses}/>{invalidFields.includes('status') && <small className="field-error-message">Status required.</small>}</div><div className="form-group"><label htmlFor="priority">Priority <span className="required-asterisk">*</span></label><SearchableDropdown options={priorities} selectedValues={selectedPriority} onChange={(value) => handleFieldChange('priority', value, setSelectedPriority)} placeholder="Select a Priority" labelField="value" valueField="value" disabled={isSubmitting || !hasPermission(PERMISSIONS.VIEW_PRIORITY)} error={invalidFields.includes('priority')} dataLoadError={dataLoadErrors.priorities}/>{invalidFields.includes('priority') && <small className="field-error-message">Priority required.</small>}</div></div>
                    
                    <div className="attachments-section-form">
                        <h2>Attachments (Optional)</h2>
                        <label 
                            htmlFor="file-upload"
                            className={`drop-zone ${isDragging ? 'drag-over' : ''}`}
                            onDrop={handleFileDrop}
                            onDragOver={handleDragOver}
                            onDragEnter={handleDragEnter}
                            onDragLeave={handleDragLeave}
                        >
                            <input 
                                id="file-upload" 
                                type="file" 
                                multiple 
                                onChange={handleFileChange} 
                                className="file-input-hidden" 
                                disabled={isSubmitting}
                            />
                            <div className="drop-zone-content">
                                <IoCloudUploadOutline />
                                <span>{isDragging ? 'Drop files here' : 'Drag & Drop files or Click to Upload'}</span>
                                <small>You can add multiple files</small>
                            </div>
                        </label>
                        {files.length > 0 && (
                            <div className="file-list">
                                {files.map(file => (
                                    <div key={file.name} className="file-list-item">
                                        <span className="file-name">{file.name}</span>
                                        <button 
                                            type="button" 
                                            className="remove-file-btn" 
                                            onClick={() => handleRemoveFile(file.name)} 
                                            title="Remove file" 
                                            disabled={isSubmitting}
                                        >
                                            <IoTrashOutline />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                <div className="form-side-panel">
                    <div className="assignment-section">
                        <h2>Assignment</h2>
                        <div className="form-group">
                            <label htmlFor="assignee">Assignee <span className="required-asterisk">*</span></label>
                            <SearchableDropdown options={formattedAssignees} selectedValues={selectedAssigneeId} onChange={handleAssigneeChange} placeholder="Select an Assignee" labelField="name" valueField="id" disabled={isAssigneeFieldDisabled} error={invalidFields.includes('assignee')} dataLoadError={dataLoadErrors.users}/>
                            {invalidFields.includes('assignee') && <small className="field-error-message">Assignee is required.</small>}
                        </div>
                        <div className="form-group">
                            <label>Department</label>
                            <input type="text" value={selectedDepartment} readOnly placeholder="Assignee's department" disabled />
                            <small>Automatically filled based on assignee.</small>
                        </div>
                    </div>
                    <div className="duedate-section">
                        <h2>Due Date</h2>
                        <div className="form-group">
                            <label>Due Date & Time <span className="required-asterisk">*</span></label>
                            <div className="custom-duedate-inputs">
                                <div className={`date-picker-dmy ${invalidFields.includes('dueDate') ? 'invalid-field' : ''}`}>
                                    <select value={dueDate.getDate().toString()} onChange={(e) => handleDateTimeChange('day', e.target.value)} disabled={isSubmitting} aria-label="Due Day">{filteredDays.map(day => <option key={day} value={day}>{day}</option>)}</select>
                                    <select value={(dueDate.getMonth() + 1).toString()} onChange={(e) => handleDateTimeChange('month', e.target.value)} disabled={isSubmitting} aria-label="Due Month">{filteredMonths.map(month => <option key={month.value} value={month.value}>{month.name}</option>)}</select>
                                    <select value={dueDate.getFullYear().toString()} onChange={(e) => handleDateTimeChange('year', e.target.value)} disabled={isSubmitting} aria-label="Due Year">{years.map(year => <option key={year} value={year}>{year}</option>)}</select>
                                </div>
                                <div className={`time-picker-24h ${invalidFields.includes('dueDate') ? 'invalid-field' : ''}`}>
                                    <select value={pad(dueDate.getHours())} onChange={(e) => handleDateTimeChange('hour', e.target.value)} disabled={isSubmitting} aria-label="Due Hour">{filteredHours.map(hour => <option key={hour} value={hour}>{hour}</option>)}</select>
                                    <span>:</span>
                                    <select value={pad(dueDate.getMinutes())} onChange={(e) => handleDateTimeChange('minute', e.target.value)} disabled={isSubmitting} aria-label="Due Minute">{filteredMinutes.map(minute => <option key={minute} value={minute}>{minute}</option>)}</select>
                                </div>
                            </div>
                            {(invalidFields.includes('dueDate') || invalidFields.includes('dueTime')) && <small className="field-error-message">A valid due date and time are required.</small>}
                        </div>
                    </div>
                    <div className="reminder-section"><h2>Reminder</h2><div className="form-group"><label htmlFor="remindMeDays">Remind Me Before Due Date In Days</label><input type="number" id="remindMeDays" value={remindMeDays} onChange={(e) => setRemindMeDays(Number(e.target.value))} min="1" max={maxReminderDays > 0 ? maxReminderDays : 1} disabled={isSubmitting || maxReminderDays <= 0} className="form-input"/><small>{maxReminderDays <= 0 ? "Reminder cannot be set for a past date." : "In days. Max: " + maxReminderDays}</small></div></div>
                </div>
                <div className="form-actions"><button type="submit" className="create-task-btn" disabled={isSubmitting || isFetchingInitialData}>{isSubmitting ? 'Creating Task...' : 'Create Task'}</button></div>
            </form>
        </div>
    );
};

export default CreateTaskPage;